import { ActionContext } from "vuex";
import * as fb from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import type { Timestamp } from "firebase/firestore";

import {
  ADD_USER_PAYMENT,
  Payment,
  DELETE_USER_PAYMENT,
  SetListenerRequest,
  SET_LISTENER,
  UPDATE_USER_PAYMENT,
  PaymentsState,
  Quote,
  ADD_USER_QUOTE,
  DELETE_USER_QUOTE,
  UPDATE_USER_QUOTE,
} from "./PaymentsInterfaces";

const snapshotListeners = {
  payments: () => {
    return;
  },
  quotes: () => {
    return;
  },
};

const initialState: PaymentsState = {
  payments: [],
  quotes: [],
};

const state: PaymentsState = initialState;

const mutations = {
  ADD_USER_PAYMENT(state: PaymentsState, payment: Payment) {
    state.payments.unshift(payment);
  },
  DELETE_USER_PAYMENT(state: PaymentsState, paymentId: number) {
    state.payments = state.payments.filter((item) => {
      return item._id != paymentId;
    });
  },
  UPDATE_USER_PAYMENT(state: PaymentsState, payment: Payment) {
    const index = state.payments.findIndex((item: any) => {
      return item._id == payment._id;
    });
    state.payments[index] = payment;
  },
  CLEAR_USER_PAYMENTS(state: PaymentsState) {
    if (state) {
      state.payments = [];
    }
  },
  ADD_USER_QUOTE(state: PaymentsState, quote: Quote) {
    // console.log("add quote");
    state.quotes.unshift(quote);
  },
  DELETE_USER_QUOTE(state: PaymentsState, quoteId: string) {
    // console.log("delete quote");
    state.quotes = state.quotes.filter((item) => {
      return item.tx_id != quoteId;
    });
  },
  UPDATE_USER_QUOTE(state: PaymentsState, quote: Quote) {
    // console.log("update quote");
    const index = state.quotes.findIndex((item: Quote) => {
      return item.tx_id == quote.tx_id;
    });
    state.quotes[index] = quote;
  },
  CLEAR_USER_QUOTES(state: PaymentsState) {
    if (state) {
      state.quotes = [];
    }
  },
  SET_LISTENER(state: PaymentsState, request: SetListenerRequest) {
    snapshotListeners[request.listenerId] = request.listener;
  },
  CLEAR_LISTENER(state: PaymentsState, listenerId: "payments" | "quotes") {
    snapshotListeners[listenerId]();
    snapshotListeners[listenerId] = function () {
      return;
    };
  },
};

const actions = {
  doGetPayments(context: ActionContext<PaymentsState, any>) {
    const username = context.rootState.auth.userProfileData._usernamePK;
    const _userPayments = query(
      collection(fb.firestore, "users", username, "payments"),
      orderBy("_id", "asc"),
      limit(100)
    );

    const l = onSnapshot(_userPayments, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        const doc = change.doc;
        if (doc.data()) {
          const payment = doc.data() as Payment;
          if (change.type == "added") {
            context.commit(ADD_USER_PAYMENT, payment);
          } else if (change.type == "modified") {
            context.commit(UPDATE_USER_PAYMENT, payment);
          }
        } else if (change.type == "removed") {
          context.commit(DELETE_USER_PAYMENT, doc.id);
        }
      });
    });

    context.commit(SET_LISTENER, { listenerId: "payments", listener: l });
  },
  doGetQuotes(context: ActionContext<PaymentsState, any>) {
    const username = context.rootState.auth.userProfileData._usernamePK;
    const _userQuotes = query(
      collection(fb.firestore, "users", username, "quotes"),
      where("expirationTime", ">", new Date()),
      where("status", "in", ["new", "paid"]),
      orderBy("expirationTime", "asc"),
      limit(100)
    );

    const l = onSnapshot(_userQuotes, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        const doc = change.doc;
        const quote = doc.data() as Quote;
        quote.createdDate = (quote.createdDate as Timestamp).toDate();
        quote.expirationTime = (quote.expirationTime as Timestamp).toDate();
        // console.log(change.type, quote.tx_id);
        if (doc.data()) {
          if (change.type == "added") {
            context.commit(ADD_USER_QUOTE, quote);
          } else if (change.type == "modified") {
            context.commit(UPDATE_USER_QUOTE, quote);
          } else if (change.type == "removed") {
            context.commit(DELETE_USER_QUOTE, quote.tx_id);
          }
        }
      });
    });

    context.commit(SET_LISTENER, { listenerId: "quotes", listener: l });
  },
  unsubscribeListener(
    context: ActionContext<PaymentsState, any>,
    listenerId: "payments" | "quotes"
  ) {
    snapshotListeners[listenerId]();
  },
};
const getters = {
  getPayments(state: PaymentsState): Array<Payment> {
    return state ? state.payments : new Array<Payment>();
  },
  getQuotes(state: PaymentsState): Array<Quote> {
    return state ? state.quotes : new Array<Quote>();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
